/**
 * 📄 MODAL SAISIE FACTURE FOURNISSEUR
 * ====================================
 * Modal pour enregistrer une facture fournisseur à partir de lignes Job Costing
 *
 * Workflow:
 * 1. Utilisateur sélectionne un fournisseur
 * 2. Coûts et débours non facturés de ce fournisseur s'affichent
 * 3. Utilisateur sélectionne les lignes à inclure
 * 4. Saisit N° facture, dates, pièce jointe (optionnel)
 * 5. Clique "Enregistrer" → Appel API backend
 * 6. Facture créée + Lignes marquées is_supplier_billed=1
 *
 * Contraintes:
 * - Coûts et débours sélectionnables indépendamment de la facturation client
 * - Lignes déjà facturées (is_supplier_billed=true) désactivées
 * - Toutes lignes doivent appartenir au même fournisseur
 * - Calcul automatique du total
 * - Support multi-dossiers automatique côté backend
 *
 * @see src/gestion-logistics/hooks/useSupplierBills.ts (recordSupplierBill)
 * @see routes/logistics/supplier-bills.js (POST /record)
 * @see PHASE-2-API-BACKEND-SUPPLIER-BILLING-COMPLETE.md
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Table,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Alert,
  message,
  Space,
  Typography,
  Tooltip,
  Upload,
  Button,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  ShopOutlined,
  CalendarOutlined,
  LockOutlined,
  UploadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import API_CONFIG from '../../config/api';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import useSupplierBills from '../hooks/useSupplierBills';
import { JobCostingLine } from '../types/costing.types';
import SupplierSelector from './SupplierSelector';
import { useSelection } from '../../SelectionContext';
import { isSupplierBillableLine } from '../utils/costingBilling';

const { TextArea } = Input;
const { Text } = Typography;

interface SupplierBillRecordModalProps {
  visible: boolean;
  onClose: () => void;
  jobCostingLines: JobCostingLine[];
  onSuccess?: () => void;
}

const SupplierBillRecordModal: React.FC<SupplierBillRecordModalProps> = ({
  visible,
  onClose,
  jobCostingLines,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { recordSupplierBill, loading } = useSupplierBills();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
  const [attachmentInfo, setAttachmentInfo] = useState<{
    url: string;
    filename: string;
    mimetype: string;
  } | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const availableSupplierIds = useMemo(() => {
    const supplierIds = new Set<number>();
    jobCostingLines.forEach((line) => {
      if (
        isSupplierBillableLine(line) &&
        typeof line.supplier_id === 'number'
      ) {
        supplierIds.add(line.supplier_id);
      }
    });
    return Array.from(supplierIds);
  }, [jobCostingLines]);

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const toNumber = (value: unknown): number => {
    if (value === null || value === undefined) return 0;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Coûts et débours non facturés du fournisseur sélectionné.
  const selectableLines = useMemo(() => {
    return jobCostingLines.filter(
      (line) =>
        isSupplierBillableLine(line) &&
        (!selectedSupplierId || line.supplier_id === selectedSupplierId)
    );
  }, [jobCostingLines, selectedSupplierId]);

  // Compter les dossiers distincts dans les lignes sélectionnées
  const selectedShipmentsInfo = useMemo(() => {
    const selectedLines = selectableLines.filter((line) =>
      selectedRowKeys.includes(line.id)
    );

    const uniqueShipments = new Set(selectedLines.map((line) => line.shipment_id));
    const isMultiShipment = uniqueShipments.size > 1;

    return {
      shipmentsCount: uniqueShipments.size,
      isMultiShipment,
    };
  }, [selectedRowKeys, selectableLines]);

  // Calculer le total en temps réel
  const totals = useMemo(() => {
    const selectedLines = selectableLines.filter((line) =>
      selectedRowKeys.includes(line.id)
    );

    const total = selectedLines.reduce((sum, line) => sum + toNumber(line.amount), 0);

    return {
      total,
      selectedCount: selectedLines.length,
    };
  }, [selectedRowKeys, selectableLines]);

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedRowKeys([]);
      setSelectedSupplierId(null);
      setSelectedSupplierName('');
      setAttachmentInfo(null);
      // Définir date par défaut = aujourd'hui
      form.setFieldsValue({
        bill_date: dayjs(),
      });
    }
  }, [visible, form]);

  // Handler changement fournisseur
  const handleSupplierChange = (supplierId: number, supplierName: string) => {
    console.log('📦 [SupplierBillRecordModal] Fournisseur sélectionné:', {
      supplierId,
      supplierName,
    });

    setSelectedSupplierId(supplierId);
    setSelectedSupplierName(supplierName);
    setSelectedRowKeys([]); // Reset sélection lignes

    form.setFieldsValue({
      supplier_id: supplierId,
      supplier_name: supplierName,
    });
  };

  // Colonnes du tableau de sélection
  const columns: ColumnsType<JobCostingLine> = [
    {
      title: 'Dossier',
      key: 'shipment',
      width: 100,
      render: (_, record) => (
        <Text strong style={{ color: '#1890ff', fontSize: '12px' }}>
          #{record.shipment_id}
        </Text>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.label_name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (qty: number, record) => `${qty || 1} ${record.unit || ''}`,
    },
    {
      title: 'Prix Unitaire',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (price: number, record) =>
        `${formatAmount(price)} ${record.currency_symbol || 'XOF'}`,
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount: number, record) => (
        <Text strong style={{ fontSize: '14px' }}>
          {formatAmount(amount)} {record.currency_symbol || 'XOF'}
        </Text>
      ),
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => {
        if (record.is_supplier_billed) {
          return (
            <Tooltip title={`Déjà facturé (${record.supplier_bill_number})`}>
              <LockOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            </Tooltip>
          );
        }
        return <Text type="secondary" style={{ fontSize: '12px' }}>Non facturé</Text>;
      },
    },
  ];

  // Configuration de la sélection
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: JobCostingLine) => ({
      disabled:
        !isSupplierBillableLine(record) ||
        (selectedSupplierId !== null && record.supplier_id !== selectedSupplierId),
      name: record.label_name,
    }),
  };

  // Validation du fichier avant upload
  const beforeUpload = (file: File) => {
    const isValidType =
      file.type === 'application/pdf' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/png';

    if (!isValidType) {
      message.error('Seuls les fichiers PDF, JPEG et PNG sont autorisés');
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Le fichier doit faire moins de 10MB');
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // Upload personnalisé vers notre API
  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    setUploadingAttachment(true);

    try {
      const formData = new FormData();
      formData.append('attachment', file);

      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/supplier-bills/upload-attachment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({ percent });
            }
          },
        }
      );

      if (response.data.success) {
        setAttachmentInfo({
          url: response.data.url,
          filename: response.data.filename,
          mimetype: response.data.mimetype,
        });
        message.success(`${file.name} uploadé avec succès`);
        onSuccess(response.data, file);
      } else {
        throw new Error(response.data.message || 'Erreur upload');
      }
    } catch (error: any) {
      console.error('❌ [SupplierBillRecordModal] Erreur upload:', error);
      message.error(error.response?.data?.message || 'Erreur lors de l\'upload du fichier');
      onError(error);
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Supprimer le fichier uploadé
  const handleRemoveAttachment = () => {
    setAttachmentInfo(null);
    message.info('Pièce jointe supprimée (locale seulement)');
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    try {
      // Validation
      if (!selectedSupplierId) {
        message.warning('Veuillez sélectionner un fournisseur');
        return;
      }

      if (selectedRowKeys.length === 0) {
        message.warning('Veuillez sélectionner au moins une ligne de coût');
        return;
      }

      const values = await form.validateFields();
      const selectedLines = selectableLines.filter((line) => selectedRowKeys.includes(line.id));

      console.log('📄 [SupplierBillRecordModal] Enregistrement facture:', {
        supplierId: selectedSupplierId,
        selectedLines: selectedRowKeys.length,
        totals,
        isMultiShipment: selectedShipmentsInfo.isMultiShipment,
      });

      // Préparer les données pour l'API
      const billData = {
        supplier_id: selectedSupplierId,
        supplier_name: selectedSupplierName,
        job_costing_line_ids: selectedRowKeys.map((key) => Number(key)),
        bill_number: values.bill_number,
        internal_reference: values.internal_reference || undefined,
        bill_date: values.bill_date ? values.bill_date.format('YYYY-MM-DD') : undefined,
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
        total_amount: totals.total,
        currency_id: selectedLines.find((line) => line.currency_id)?.currency_id,
        warehouse_id: selectedWarehouse || undefined,
        notes: values.notes || undefined,
        // Pièce jointe (si uploadée)
        attachment_url: attachmentInfo?.url || undefined,
        attachment_filename: attachmentInfo?.filename || undefined,
        attachment_mime_type: attachmentInfo?.mimetype || undefined,
      };

      // Appeler le hook
      const result = await recordSupplierBill(billData);

      if (result) {
        message.success('Facture fournisseur enregistrée avec succès !');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('❌ [SupplierBillRecordModal] Erreur validation:', error);
      message.error('Erreur lors de l\'enregistrement de la facture');
    }
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Saisir une Facture Fournisseur</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1100}
      okText="Enregistrer la facture"
      cancelText="Annuler"
      onOk={handleSubmit}
      confirmLoading={loading}
      destroyOnClose
    >
      {/* Alerte informative */}
      <Alert
        message="Sélectionnez un fournisseur puis les lignes de coût à facturer"
        description="Seules les lignes de type 'Cost' non encore facturées fournisseur peuvent être sélectionnées. Une facture peut couvrir plusieurs dossiers."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Formulaire sélection fournisseur */}
      <Card title="Fournisseur" size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Fournisseur"
                name="supplier_id"
                rules={[{ required: true, message: 'Fournisseur requis' }]}
              >
                <SupplierSelector
                  onChange={handleSupplierChange}
                  companyId={selectedCompany}
                  warehouseId={selectedWarehouse}
                  allowedSupplierIds={availableSupplierIds}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Nom du Fournisseur"
                name="supplier_name"
                tooltip="Rempli automatiquement après sélection du fournisseur"
              >
                <Input
                  placeholder="Sélectionnez un fournisseur"
                  disabled
                  prefix={<ShopOutlined />}
                  style={{
                    cursor: 'not-allowed',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Tableau de sélection des lignes */}
      {selectedSupplierId && (
        <>
          <Card
            title={`Lignes de coût - ${selectedSupplierName}`}
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={selectableLines}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ y: 250 }}
              locale={{
                emptyText:
                  'Aucune ligne de coût non facturée pour ce fournisseur',
              }}
            />
          </Card>

          {/* Aperçu des totaux */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Lignes sélectionnées"
                  value={totals.selectedCount}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Dossiers couverts"
                  value={selectedShipmentsInfo.shipmentsCount}
                  prefix={<ShopOutlined />}
                  valueStyle={{
                    color: selectedShipmentsInfo.isMultiShipment
                      ? '#ff4d4f'
                      : '#52c41a',
                  }}
                  suffix={
                    selectedShipmentsInfo.isMultiShipment && (
                      <Tooltip title="Cette facture couvrira plusieurs dossiers">
                        <Text type="warning" style={{ fontSize: '12px' }}>
                          (Multi)
                        </Text>
                      </Tooltip>
                    )
                  }
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="TOTAL"
                  value={totals.total}
                  formatter={(value) => `${formatAmount(value as number)} XOF`}
                  prefix={<DollarOutlined />}
                  valueStyle={{
                    color: '#1890ff',
                    fontSize: '20px',
                    fontWeight: 'bold',
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Divider>Détails de la facture</Divider>

          {/* Formulaire détails facture */}
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="N° Facture Fournisseur"
                  name="bill_number"
                  rules={[
                    {
                      required: true,
                      message: 'Numéro de facture requis',
                    },
                  ]}
                  tooltip="Numéro de facture du fournisseur (tel que reçu)"
                >
                  <Input
                    placeholder="Ex: FAC-2025-001"
                    prefix={<FileTextOutlined />}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Référence Interne"
                  name="internal_reference"
                  tooltip="Référence interne pour suivi (optionnel)"
                >
                  <Input placeholder="Ex: REF-INT-123" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date de facture"
                  name="bill_date"
                  rules={[
                    { required: true, message: 'Date de facture requise' },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Sélectionner la date"
                    prefix={<CalendarOutlined />}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Date d'échéance" name="due_date">
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Date d'échéance (optionnel)"
                    prefix={<CalendarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Pièce jointe */}
            <Form.Item
              label="Pièce jointe"
              tooltip="Upload de la facture fournisseur (PDF, JPEG, PNG - max 10MB)"
            >
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                maxCount={1}
                beforeUpload={beforeUpload}
                customRequest={customRequest}
                onRemove={handleRemoveAttachment}
                fileList={
                  attachmentInfo
                    ? [
                        {
                          uid: '-1',
                          name: attachmentInfo.filename,
                          status: 'done',
                        },
                      ]
                    : []
                }
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
              >
                {!attachmentInfo && (
                  <Button icon={<UploadOutlined />} loading={uploadingAttachment}>
                    {uploadingAttachment
                      ? 'Upload en cours...'
                      : 'Cliquer ou glisser le fichier ici'}
                  </Button>
                )}
              </Upload>
              {attachmentInfo && (
                <div style={{ marginTop: 8 }}>
                  <Space>
                    <PaperClipOutlined style={{ color: '#52c41a' }} />
                    <Text type="success" style={{ fontSize: '12px' }}>
                      Fichier prêt : {attachmentInfo.filename}
                    </Text>
                  </Space>
                </div>
              )}
            </Form.Item>

            {/* Notes */}
            <Form.Item label="Notes" name="notes">
              <TextArea
                rows={3}
                placeholder="Notes ou remarques pour cette facture fournisseur..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>

          {/* Récapitulatif final */}
          {selectedRowKeys.length > 0 && (
            <Card
              size="small"
              style={{
                backgroundColor: '#f0f5ff',
                border: '1px solid #adc6ff',
              }}
            >
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: '14px' }}>
                      {totals.selectedCount} ligne(s) sélectionnée(s)
                    </Text>
                    {selectedShipmentsInfo.isMultiShipment && (
                      <Text type="warning" style={{ fontSize: '12px' }}>
                        Couvre {selectedShipmentsInfo.shipmentsCount} dossier
                        (s)
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                    TOTAL: {formatAmount(totals.total)} XOF
                  </Text>
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}
    </Modal>
  );
};

export default SupplierBillRecordModal;
